
import throttle from '~/services/throttle';

export default {
    name: 'MediaViewer',
    props: {
        /**
         * Should the viewer fill the screen
         */
        fullscreen: {
            type: Boolean,
            default: true,
        },
        /**
         * An array of media
         */
        media: {
            type: Array,
            required: true,
        },
        /**
         * Index of array to show first
         */
        startIndex: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            addCounter: 0,
            currentSlide: 0,
            drag: {
                startX: 0,
                endX: 0,
                startY: 0,
                letItGo: null,
                preventClick: false,
            },
            width: 0,
            innerElements: null,
            perPage: 1,
            pointerDown: false,
            throttledResize: throttle(this.handleResize, 100),
            transformProperty: 'transform',
        };
    },
    computed: {
        threshold() {
            return this.width / 4;
        },
        videoSize() {
            let size = 'small';
            if (window.innerWidth > 1300) {
                size = 'large';
            } else if (window.innerWidth > 900) {
                size = 'medium';
            }
            return size;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.currentSlide = Math.max(
                0,
                Math.min(this.startIndex, this.media.length - this.perPage),
            );
            this.$nextTick(() => {
                this.width = this.$el.clientWidth;
                this.slideToCurrent();
            });
        });
        this.transformProperty = this.webkitOrNot();
        window.addEventListener('resize', this.throttledResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.throttledResize);
    },
    methods: {
        webkitOrNot() {
            const { style } = document.documentElement;
            if (typeof style.transform === 'string') {
                return 'transform';
            }
            return 'WebkitTransform';
        },
        clearDrag() {
            this.drag = {
                startX: 0,
                endX: 0,
                startY: 0,
                letItGo: null,
                preventClick: this.drag.preventClick,
            };
        },
        close(e) {
            if (e.target.tagName !== 'IMG') {
                this.$emit('input', false);
            }
        },
        stopAllVideos() {
            const $allVideos = document.querySelectorAll('.slide video');
            for (let i = 0; i < $allVideos.length; i += 1) {
                $allVideos[i].pause();
            }
        },
        slideToCurrent(enableTransition) {
            const offset = -1 * this.currentSlide * (this.width / this.perPage);

            if (enableTransition) {
                // This one is tricky, I know but this is a perfect explanation:
                // https://youtu.be/cCOL7MC4Pl0
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.enableTransition();
                        this.$refs.slider.style[this.transformProperty] = `translate3d(${offset}px, 0, 0)`;
                    });
                });
            } else {
                this.$refs.slider.style[this.transformProperty] = `translate3d(${offset}px, 0, 0)`;
            }
            /**
             * The current slide has changed
             *
             * @type {int}
             */
            this.$emit('index', this.currentSlide);

            this.$nextTick(() => {
                const $activeVideo = document.querySelector('.slide.active video');
                this.stopAllVideos();
                if ($activeVideo) {
                    $activeVideo.load();
                    $activeVideo.play();
                }
            });
        },
        mousedownHandler(e) {
            this.pointerDown = true;
            this.drag.startX = e.pageX;
        },
        mouseupHandler() {
            this.pointerDown = false;
            this.enableTransition();
            if (this.drag.endX) {
                this.updateAfterDrag();
            }
            this.clearDrag();
        },
        mousemoveHandler(e) {
            if (this.pointerDown && this.media.length > 1) {
                this.drag.endX = e.pageX;
                this.$refs.slider.style.webkitTransition = 'all 0ms ease-out';
                this.$refs.slider.style.transition = 'all 0ms ease-out';

                const currentOffset = this.currentSlide * (this.width / this.perPage);
                const dragOffset = (this.drag.endX - this.drag.startX);
                const offset = currentOffset - dragOffset;
                this.$refs.slider.style[this.transformProperty] = `translate3d(${-1 * offset}px, 0, 0)`;
            }
        },
        mouseleaveHandler(e) {
            if (this.pointerDown) {
                this.pointerDown = false;
                this.drag.endX = e.pageX;
                this.drag.preventClick = false;
                this.enableTransition();
                this.updateAfterDrag();
                this.clearDrag();
            }
        },
        touchstartHandler(e) {
            this.pointerDown = true;
            this.drag.startX = e.touches[0].pageX;
            this.drag.startY = e.touches[0].pageY;
        },
        touchmoveHandler(e) {
            if (this.drag.letItGo === null) {
                this.drag.letItGo = Math.abs(this.drag.startY - e.touches[0].pageY) < Math.abs(this.drag.startX - e.touches[0].pageX);
            }

            if (this.pointerDown && this.drag.letItGo && this.media.length > 1) {
                this.drag.endX = e.touches[0].pageX;
                this.$refs.slider.style.webkitTransition = 'all 0ms ease-out';
                this.$refs.slider.style.transition = 'all 0ms ease-out';

                const currentOffset = this.currentSlide * (this.width / this.perPage);
                const dragOffset = this.drag.endX - this.drag.startX;
                const offset = currentOffset - dragOffset;
                this.$refs.slider.style[this.transformProperty] = `translate3d(${-1 * offset}px, 0, 0)`;
            }
        },
        touchendHandler() {
            this.pointerDown = false;
            this.enableTransition();
            if (this.drag.endX) {
                this.updateAfterDrag();
            }
            this.clearDrag();
        },
        enableTransition() {
            this.$refs.slider.style.webkitTransition = 'all 200ms ease-out';
            this.$refs.slider.style.transition = 'all 200ms ease-out';
        },
        /**
         * Recalculate drag /swipe event and reposition the frame of a slider
         */
        updateAfterDrag() {
            const movement = 1 * (this.drag.endX - this.drag.startX);
            const movementDistance = Math.abs(movement);
            const howManySliderToSlide = Math.ceil(movementDistance / (this.width / this.perPage));

            const slideToNegativeClone = movement > 0 && this.currentSlide - howManySliderToSlide < 0;
            const slideToPositiveClone = movement < 0 && this.currentSlide + howManySliderToSlide > this.media.length - this.perPage;

            if (movement > 0 && movementDistance > this.threshold && this.media.length > this.perPage) {
                this.prev(howManySliderToSlide);
            } else if (movement < 0 && movementDistance > this.threshold && this.media.length > this.perPage) {
                this.next(howManySliderToSlide);
            }
            this.slideToCurrent(slideToNegativeClone || slideToPositiveClone);
        },
        /**
         * Go to previous slide.
         * @param {number} [howManySlides=1] - How many items to slide backward.
         */
        prev(howManySlides = 1) {
            // early return when there is nothing to slide
            if (this.media.length < this.perPage) {
                return;
            }

            const beforeChange = this.currentSlide;
            this.currentSlide = Math.max(this.currentSlide - howManySlides, 0);
            if (beforeChange !== this.currentSlide) {
                this.slideToCurrent(false);
            }
        },
        /**
         * Go to next slide.
         * @param {number} [howManySlides=1] - How many items to slide forward.
         */
        next(howManySlides = 1) {
            // early return when there is nothing to slide
            if (this.media.length <= this.perPage) {
                return;
            }

            const beforeChange = this.currentSlide;
            this.currentSlide = Math.min(this.currentSlide + howManySlides, this.media.length - this.perPage);
            if (beforeChange !== this.currentSlide) {
                this.slideToCurrent(false);
            }
        },
        handleResize() {
            this.width = this.$el.clientWidth;
            this.$nextTick(() => {
                this.slideToCurrent(false);
            });
        },
    },
};
